import React, { useState, useEffect } from 'react';
import { Trophy, Gift, Users, AlertTriangle, CheckCircle, Clock, Calendar, RefreshCw, ChevronRight, MapPin, Crown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../lib/api';

const AdminRewards = () => {
  const [stats, setStats] = useState(null);
  const [winners, setWinners] = useState([]);
  const [pendingPrizes, setPendingPrizes] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('winners');
  const [triggeringCalc, setTriggeringCalc] = useState(false);
  const [hubLeaderboards, setHubLeaderboards] = useState([]);
  const [leaderboardMonth, setLeaderboardMonth] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
    fetchHubLeaderboards(); // Fetch hub leaderboards on mount
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchWinners(selectedMonth);
    }
  }, [selectedMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, monthsRes, pendingRes, flaggedRes] = await Promise.all([
        adminApi.get(`/admin/rewards/stats`),
        adminApi.get(`/admin/rewards/months`),
        adminApi.get(`/admin/rewards/pending`),
        adminApi.get(`/admin/rewards/flagged-users`)
      ]);

      setStats(statsRes.data);
      setAvailableMonths(monthsRes.data.months || []);
      setPendingPrizes(pendingRes.data.pending_prizes || []);
      setFlaggedUsers(flaggedRes.data.flagged_users || []);

      if (monthsRes.data.months?.length > 0) {
        setSelectedMonth(monthsRes.data.months[0]);
      }
    } catch (err) {
      console.error('Error fetching rewards data:', err);
      toast({ title: 'Error', description: 'Failed to load rewards data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchHubLeaderboards = async (month = null) => {
    try {
      const url = month 
        ? `/admin/rewards/hub-leaderboards?month_key=${month}`
        : `/admin/rewards/hub-leaderboards`;
      const res = await adminApi.get(url);
      setHubLeaderboards(res.data.hubs || []);
      setLeaderboardMonth(res.data.month_key || '');
    } catch (err) {
      console.error('Error fetching hub leaderboards:', err);
    }
  };

  const fetchWinners = async (month) => {
    try {
      const res = await adminApi.get(`/admin/rewards/winners?month_key=${month}`);
      setWinners(res.data.winners || []);
    } catch (err) {
      console.error('Error fetching winners:', err);
    }
  };

  const updatePrizeStatus = async (winnerId, place, newStatus) => {
    try {
      await adminApi.patch(`/admin/rewards/prize-status`, {
        winner_record_id: winnerId,
        place: place,
        status: newStatus
      });

      toast({ title: 'Success', description: `Prize status updated to ${newStatus}` });
      fetchData();
    } catch (err) {
      console.error('Error updating prize status:', err);
      toast({ title: 'Error', description: 'Failed to update prize status', variant: 'destructive' });
    }
  };

  const reviewFlag = async (flagId, action) => {
    try {
      await adminApi.post(`/admin/rewards/review-flag`, {
        flag_id: flagId,
        action: action,
        notes: `Action taken: ${action}`
      });

      toast({ title: 'Success', description: `Flag ${action}ed successfully` });
      fetchData();
    } catch (err) {
      console.error('Error reviewing flag:', err);
      toast({ title: 'Error', description: 'Failed to review flag', variant: 'destructive' });
    }
  };

  const triggerWinnerCalculation = async () => {
    if (!window.confirm('This will calculate winners for the previous month and send notification emails. Continue?')) {
      return;
    }

    try {
      setTriggeringCalc(true);
      const res = await adminApi.post(`/admin/rewards/trigger-calculation`, {});

      toast({ 
        title: 'Success', 
        description: `Winner calculation complete! ${res.data.results?.winners_calculated || 0} winners notified.`
      });
      fetchData();
    } catch (err) {
      console.error('Error triggering calculation:', err);
      toast({ title: 'Error', description: 'Failed to calculate winners', variant: 'destructive' });
    } finally {
      setTriggeringCalc(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'notified': return 'bg-blue-100 text-blue-700';
      case 'claimed': return 'bg-purple-100 text-purple-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatMonth = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-consumer border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono text-consumer text-sm mb-1">// admin.rewards</p>
          <h1 className="font-display text-3xl text-navy">Rewards Management</h1>
          <p className="text-navy/50 mt-1">Manage leaderboard winners and prizes</p>
        </div>
        <button
          onClick={triggerWinnerCalculation}
          disabled={triggeringCalc}
          className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-orange-500/20 transition-all flex items-center gap-2 disabled:opacity-50"
        >
          {triggeringCalc ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              <Trophy className="w-4 h-4" />
              Calculate Winners
            </>
          )}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat-card purple bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
            <Trophy className="w-6 h-6 text-purple-600" />
          </div>
          <p className="text-4xl font-bold text-navy">{stats?.total_winner_records || 0}</p>
          <p className="text-navy/50 text-sm">Total Winners</p>
        </div>

        <div className="stat-card yellow bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
          <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
            <Clock className="w-6 h-6 text-yellow-600" />
          </div>
          <p className="text-4xl font-bold text-navy">{stats?.pending_prizes || 0}</p>
          <p className="text-navy/50 text-sm">Pending Prizes</p>
        </div>

        <div className="stat-card green bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-4xl font-bold text-navy">{stats?.delivered_prizes || 0}</p>
          <p className="text-navy/50 text-sm">Delivered</p>
        </div>

        <div className="stat-card red bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-4xl font-bold text-navy">{flaggedUsers.length}</p>
          <p className="text-navy/50 text-sm">Flagged Users</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('winners')}
          className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
            activeTab === 'winners' 
              ? 'tab-active text-navy' 
              : 'text-navy/60 hover:bg-white/50'
          }`}
        >
          <Trophy className="w-4 h-4 text-purple-500" />
          Winners
        </button>
        <button
          onClick={() => setActiveTab('leaderboards')}
          className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
            activeTab === 'leaderboards' 
              ? 'text-white' 
              : 'text-navy/60 hover:bg-white/50'
          }`}
          style={activeTab === 'leaderboards' ? {
            backgroundColor: '#14b8a6',
            boxShadow: '0 4px 15px rgba(20, 184, 166, 0.5), 0 0 20px rgba(20, 184, 166, 0.3)'
          } : {}}
        >
          <MapPin className="w-4 h-4" />
          Hub Leaderboards
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
            activeTab === 'pending' 
              ? 'tab-active text-navy' 
              : 'text-navy/60 hover:bg-white/50'
          }`}
        >
          <Gift className="w-4 h-4 text-yellow-500" />
          Pending Prizes ({pendingPrizes.length})
        </button>
        <button
          onClick={() => setActiveTab('flagged')}
          className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
            activeTab === 'flagged' 
              ? 'tab-active text-navy' 
              : 'text-navy/60 hover:bg-white/50'
          }`}
        >
          <AlertTriangle className="w-4 h-4 text-red-500" />
          Flagged Users ({flaggedUsers.length})
        </button>
      </div>

      {/* Content Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-navy/5 overflow-hidden">
        {activeTab === 'winners' && (
          <div className="p-6">
            {/* Month Selector */}
            <div className="flex items-center gap-4 mb-6">
              <Calendar className="w-5 h-5 text-navy/40" />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-4 py-2.5 bg-cream border border-navy/10 rounded-xl text-navy focus:outline-none focus:border-consumer transition-colors"
              >
                {availableMonths.length === 0 && (
                  <option value="">No winners yet</option>
                )}
                {availableMonths.map(month => (
                  <option key={month} value={month}>{formatMonth(month)}</option>
                ))}
              </select>
            </div>

            {winners.length === 0 ? (
              <div className="py-20 text-center">
                <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Trophy className="w-10 h-10 text-navy/20" />
                </div>
                <h3 className="font-display text-xl text-navy mb-2">No winners yet</h3>
                <p className="text-navy/50">Winners are calculated on the 1st of each month at 6 AM UTC</p>
              </div>
            ) : (
              <div className="space-y-6">
                {winners.map((record) => (
                  <div key={record.id} className="border border-navy/10 rounded-xl p-6">
                    <h3 className="font-display text-lg text-navy mb-4">{record.hub_name}</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {['first_place', 'second_place', 'third_place'].map((place, idx) => {
                        const winner = record[place];
                        if (!winner) return null;
                        
                        const emoji = ['🥇', '🥈', '🥉'][idx];
                        const placeLabel = ['1st Place', '2nd Place', '3rd Place'][idx];
                        
                        return (
                          <div key={place} className="bg-cream rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-2xl">{emoji}</span>
                              <span className="font-semibold text-navy">{placeLabel}</span>
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <p><span className="text-navy/50">Name:</span> <span className="text-navy">{winner.user_name}</span></p>
                              <p><span className="text-navy/50">Email:</span> <span className="text-navy">{winner.user_email}</span></p>
                              <p><span className="text-navy/50">Taps:</span> <span className="text-navy font-medium">{winner.total_taps?.toLocaleString()}</span></p>
                              <p><span className="text-navy/50">Prize:</span> <span className="text-navy font-bold">${winner.prize_amount}</span></p>
                              
                              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-navy/10">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(winner.prize_status)}`}>
                                  {winner.prize_status}
                                </span>
                                
                                {winner.prize_status !== 'delivered' && (
                                  <select
                                    value=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        updatePrizeStatus(record.id, place, e.target.value);
                                      }
                                    }}
                                    className="text-xs px-2 py-1 border border-navy/20 rounded-lg bg-white focus:outline-none"
                                  >
                                    <option value="">Update...</option>
                                    {winner.prize_status === 'pending' && <option value="notified">Mark Notified</option>}
                                    {winner.prize_status === 'notified' && <option value="claimed">Mark Claimed</option>}
                                    {(winner.prize_status === 'notified' || winner.prize_status === 'claimed') && (
                                      <option value="delivered">Mark Delivered</option>
                                    )}
                                  </select>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'leaderboards' && (
          <div className="p-6">
            {/* Month Display */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-navy/40" />
                <span className="font-medium text-navy">
                  {leaderboardMonth ? formatMonth(leaderboardMonth) : 'Current Month'}
                </span>
              </div>
              <button
                onClick={() => fetchHubLeaderboards()}
                className="px-4 py-2 bg-teal-50 text-teal-600 rounded-lg font-medium text-sm hover:bg-teal-100 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {hubLeaderboards.length === 0 ? (
              <div className="py-20 text-center">
                <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MapPin className="w-10 h-10 text-navy/20" />
                </div>
                <h3 className="font-display text-xl text-navy mb-2">No leaderboard data yet</h3>
                <p className="text-navy/50">Users will appear here once they start earning Taps at hubs</p>
              </div>
            ) : (
              <div className="space-y-6">
                {hubLeaderboards.map((hub) => (
                  <div key={hub.hub_id} className="border border-navy/10 rounded-xl overflow-hidden">
                    {/* Hub Header */}
                    <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-4 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            <MapPin className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-display text-lg font-semibold">{hub.hub_name}</h3>
                            {hub.location && (
                              <p className="text-white/70 text-sm">{hub.location}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">{hub.total_taps?.toLocaleString() || 0}</p>
                          <p className="text-white/70 text-sm">Total Taps</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-white/20 flex gap-6 text-sm">
                        <span><strong>{hub.total_participants}</strong> participants</span>
                        <span><strong>{hub.entries?.length || 0}</strong> on leaderboard</span>
                      </div>
                    </div>

                    {/* Leaderboard Entries */}
                    <div className="p-4">
                      {hub.entries && hub.entries.length > 0 ? (
                        <div className="space-y-2">
                          {hub.entries.map((entry, idx) => (
                            <div 
                              key={entry.user_id} 
                              className={`flex items-center gap-4 p-3 rounded-lg ${
                                idx < 3 ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200' : 'bg-cream'
                              }`}
                            >
                              {/* Rank */}
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                idx === 0 ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white' :
                                idx === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-white' :
                                idx === 2 ? 'bg-gradient-to-r from-orange-400 to-amber-600 text-white' :
                                'bg-navy/10 text-navy/60'
                              }`}>
                                {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : entry.rank}
                              </div>

                              {/* Avatar */}
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
                                {entry.picture ? (
                                  <img src={entry.picture} alt={entry.display_name} className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-white font-bold text-sm">
                                    {entry.display_name?.charAt(0)?.toUpperCase() || '?'}
                                  </span>
                                )}
                              </div>

                              {/* User Info */}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-navy truncate">{entry.display_name}</p>
                                {entry.email && (
                                  <p className="text-xs text-navy/50 truncate">{entry.email}</p>
                                )}
                              </div>

                              {/* Taps */}
                              <div className="text-right">
                                <p className="font-bold text-navy">{entry.total_taps?.toLocaleString()}</p>
                                <p className="text-xs text-navy/50">Taps</p>
                              </div>

                              {/* Crown for top 3 */}
                              {idx < 3 && (
                                <Crown className={`w-5 h-5 ${
                                  idx === 0 ? 'text-yellow-500' :
                                  idx === 1 ? 'text-gray-400' :
                                  'text-orange-500'
                                }`} />
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-navy/50 py-4">No users on leaderboard yet</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'pending' && (
          <div className="p-6">
            {pendingPrizes.length === 0 ? (
              <div className="py-20 text-center">
                <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-navy/20" />
                </div>
                <h3 className="font-display text-xl text-navy mb-2">No pending prizes</h3>
                <p className="text-navy/50">All prizes have been delivered!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingPrizes.map((record) => (
                  <div key={record.id} className="border border-navy/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-navy">{record.hub_name}</h4>
                      <span className="text-sm text-navy/50">{formatMonth(record.month_key)}</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {['first_place', 'second_place', 'third_place'].map((place, idx) => {
                        const winner = record[place];
                        if (!winner || winner.prize_status === 'delivered') return null;
                        
                        return (
                          <div key={place} className="flex items-center justify-between bg-cream p-3 rounded-lg">
                            <div>
                              <p className="font-medium text-sm text-navy">{['🥇', '🥈', '🥉'][idx]} {winner.user_name}</p>
                              <p className="text-xs text-navy/50">${winner.prize_amount}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(winner.prize_status)}`}>
                              {winner.prize_status}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'flagged' && (
          <div className="p-6">
            {flaggedUsers.length === 0 ? (
              <div className="py-20 text-center">
                <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-navy/20" />
                </div>
                <h3 className="font-display text-xl text-navy mb-2">No flagged users</h3>
                <p className="text-navy/50">All users are in good standing</p>
              </div>
            ) : (
              <div className="space-y-4">
                {flaggedUsers.map((flag) => (
                  <div key={flag.id} className="border border-red-200 bg-red-50/50 rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                          <span className="font-semibold text-red-800">{flag.flag_type}</span>
                        </div>
                        <p className="text-sm text-navy mb-1">
                          <span className="text-navy/50">User:</span> {flag.user_name || flag.user_email}
                        </p>
                        <p className="text-sm text-navy/70">{flag.details}</p>
                        <p className="text-xs text-navy/40 mt-2">
                          Flagged: {new Date(flag.flagged_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      {!flag.reviewed && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => reviewFlag(flag.id, 'dismiss')}
                            className="px-3 py-1.5 border border-green-300 text-green-600 text-sm rounded-lg hover:bg-green-50 transition-colors"
                          >
                            Dismiss
                          </button>
                          <button
                            onClick={() => reviewFlag(flag.id, 'ban')}
                            className="px-3 py-1.5 border border-red-300 text-red-600 text-sm rounded-lg hover:bg-red-100 transition-colors"
                          >
                            Ban User
                          </button>
                        </div>
                      )}
                      
                      {flag.reviewed && (
                        <span className="px-3 py-1 bg-navy/10 text-navy/60 rounded-full text-sm">
                          {flag.action_taken}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="mt-8 bg-gradient-to-r from-navy to-[#2d2a5e] rounded-2xl p-6">
        <h3 className="font-display text-xl text-white mb-4">How Rewards Work</h3>
        <div className="grid md:grid-cols-3 gap-4 text-white/80 text-sm">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-lg">🏆</span>
            </div>
            <div>
              <p className="font-medium text-white">Monthly Leaderboards</p>
              <p className="text-white/60">Top 3 users per hub win prizes each month</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-lg">💰</span>
            </div>
            <div>
              <p className="font-medium text-white">Prize Structure</p>
              <p className="text-white/60">1st: $100 • 2nd: $50 • 3rd: $25</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-lg">📧</span>
            </div>
            <div>
              <p className="font-medium text-white">Auto Notifications</p>
              <p className="text-white/60">Winners notified via email on the 1st</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRewards;
